import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, Minus, Plus, ScanBarcode, ShoppingCart, Trash2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import {
  createRetailTransactionRecord,
  fetchRetailAssociates,
  fetchRetailCatalog,
  fetchRetailRegisters,
} from "@/lib/api/retail";
import type { RetailProduct, RetailProductVariant } from "@/features/retail/types/datasets";

type RetailQuickSaleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  initialMode?: "standard" | "scan";
};

type CartItem = {
  productId: string;
  variantId: string | null;
  name: string;
  unitPriceCents: number;
  quantity: number;
};

const findProductByBarcode = (
  products: RetailProduct[],
  barcode: string,
): { product: RetailProduct; variant: RetailProductVariant | null } | null => {
  const normalized = barcode.trim();
  if (!normalized) {
    return null;
  }
  for (const product of products) {
    if (product.barcode && product.barcode === normalized) {
      return { product, variant: null };
    }
    for (const variant of product.variants) {
      if (variant.barcode && variant.barcode === normalized) {
        return { product, variant };
      }
    }
  }
  return null;
};

export const RetailQuickSaleDialog = ({
  open,
  onOpenChange,
  onSuccess,
  initialMode = "standard",
}: RetailQuickSaleDialogProps) => {
  const { t } = useTranslation(["common", "retail"]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const { formatCurrency } = useLocaleFormatter();

  const catalogQuery = useQuery({
    queryKey: ["retail", "catalog"],
    queryFn: ({ signal }) => fetchRetailCatalog({ signal }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const associatesQuery = useQuery({
    queryKey: ["retail", "associates"],
    queryFn: ({ signal }) => fetchRetailAssociates({ signal }),
    enabled: open,
    staleTime: 60 * 1000,
  });
  const registersQuery = useQuery({
    queryKey: ["retail", "registers"],
    queryFn: ({ signal }) => fetchRetailRegisters({ signal }),
    enabled: open,
    staleTime: 60 * 1000,
  });

  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<string>("1");
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [selectedAssociateId, setSelectedAssociateId] = useState<string | null>(null);
  const [selectedRegisterId, setSelectedRegisterId] = useState<string | null>(null);
  const [tenderMethod, setTenderMethod] = useState<"card" | "cash" | "gift" | "store_credit">("card");
  const [barcode, setBarcode] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedProductId(null);
      setQuantity("1");
      setCartItems([]);
      setBarcode("");
      setSelectedAssociateId(null);
      setSelectedRegisterId(null);
      setTenderMethod("card");
      return;
    }
    if (initialMode === "scan") {
      // focus after content animation completes
      const timer = setTimeout(() => barcodeInputRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    }
  }, [open, initialMode]);

  const products = useMemo(
    () => catalogQuery.data ?? [],
    [catalogQuery.data],
  );
  const productById = useMemo(() => {
    const map = new Map<string, RetailProduct>();
    for (const product of products) {
      map.set(product.id, product);
    }
    return map;
  }, [products]);

  const associates = useMemo(
    () => (associatesQuery.data ?? []).filter((associate) => associate.isActive),
    [associatesQuery.data],
  );
  const registers = useMemo(
    () => (registersQuery.data ?? []).filter((register) => register.status === "online"),
    [registersQuery.data],
  );

  useEffect(() => {
    if (associates.length > 0 && !selectedAssociateId) {
      setSelectedAssociateId(associates[0].id);
    }
  }, [associates, selectedAssociateId]);

  useEffect(() => {
    if (registers.length > 0 && !selectedRegisterId) {
      setSelectedRegisterId(registers[0].id);
    }
  }, [registers, selectedRegisterId]);

  const productOptions = useMemo(
    () => products.slice().sort((left, right) => left.name.localeCompare(right.name)),
    [products],
  );

  const handleAddToCart = (product: RetailProduct, variant: RetailProductVariant | null, quantityToAdd = 1) => {
    setCartItems((current) => {
      const existing = current.find(
        (item) => item.productId === product.id && item.variantId === (variant?.id ?? null),
      );
      const name = variant ? `${product.name} · ${variant.name}` : product.name;
      const unitPrice = variant ? variant.priceCents : product.defaultPriceCents;

      if (existing) {
        return current.map((item) =>
          item === existing
            ? { ...item, quantity: item.quantity + Math.max(1, quantityToAdd) }
            : item,
        );
      }

      return [
        ...current,
        {
          productId: product.id,
          variantId: variant?.id ?? null,
          name,
          unitPriceCents: unitPrice,
          quantity: Math.max(1, quantityToAdd),
        },
      ];
    });
  };

  const handleManualAdd = () => {
    if (!selectedProductId) {
      toast({
        title: t("retail.quickActions.sale.productRequiredTitle", { defaultValue: "Select a product" }),
        description: t("retail.quickActions.sale.productRequiredDescription", {
          defaultValue: "Choose an item before recording the sale.",
        }),
        variant: "destructive",
      });
      return;
    }

    const product = productById.get(selectedProductId);
    if (!product) {
      return;
    }

    const parsedQuantity = Number(quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      toast({
        title: t("retail.quickActions.sale.invalidQuantityTitle", { defaultValue: "Invalid quantity" }),
        description: t("retail.quickActions.sale.invalidQuantityDescription", {
          defaultValue: "Enter a positive number of units.",
        }),
        variant: "destructive",
      });
      return;
    }

    handleAddToCart(product, null, Math.round(parsedQuantity));
    setQuantity("1");
  };

  const handleQuantityChange = (productId: string, variantId: string | null, delta: number) => {
    setCartItems((current) =>
      current
        .map((item) =>
          item.productId === productId && item.variantId === variantId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item,
        )
        .filter((item) => item.quantity > 0),
    );
  };

  const handleRemoveItem = (productId: string, variantId: string | null) => {
    setCartItems((current) =>
      current.filter((item) => item.productId !== productId || item.variantId !== variantId),
    );
  };

  const handleScanSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = findProductByBarcode(products, barcode);
    if (!result) {
      toast({
        title: t("retail.quickActions.sale.scanNotFoundTitle", { defaultValue: "Barcode not recognised" }),
        description: t("retail.quickActions.sale.scanNotFoundDescription", {
          defaultValue: "Scan a product that exists in the catalog.",
        }),
        variant: "destructive",
      });
      return;
    }
    handleAddToCart(result.product, result.variant, 1);
    toast({
      title: t("retail.quickActions.sale.scanSuccessTitle", { defaultValue: "Item added" }),
      description: result.variant
        ? `${result.product.name} · ${result.variant.name}`
        : result.product.name,
    });
    setBarcode("");
    barcodeInputRef.current?.focus();
  };

  const cartSubtotal = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity * item.unitPriceCents, 0),
    [cartItems],
  );

  const createTransactionMutation = useMutation({
    mutationFn: createRetailTransactionRecord,
    onSuccess: (transaction) => {
      toast({
        title: t("retail.quickActions.sale.toastSuccessTitle", { defaultValue: "Sale recorded" }),
        description: t("retail.quickActions.sale.toastSuccessDescription", {
          receipt: transaction.receiptNumber,
          defaultValue: "Receipt {{receipt}}",
        }),
      });
      setCartItems([]);
      setQuantity("1");
      setBarcode("");
      queryClient.invalidateQueries({ queryKey: ["retail", "transactions"] });
      queryClient.invalidateQueries({ queryKey: ["retail", "dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["retail", "checkout"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast({
        title: t("status.error", { defaultValue: "Something went wrong" }),
        description:
          error instanceof Error
            ? error.message
            : t("status.tryAgain", { defaultValue: "Please try again." }),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (cartItems.length === 0) {
      toast({
        title: t("retail.quickActions.sale.cartEmptyTitle", { defaultValue: "Add an item first" }),
        description: t("retail.quickActions.sale.cartEmptyDescription", {
          defaultValue: "Scan or select at least one product before completing the sale.",
        }),
        variant: "destructive",
      });
      return;
    }
    if (createTransactionMutation.isPending) {
      return;
    }

    createTransactionMutation.mutate({
      registerId: selectedRegisterId,
      associateId: selectedAssociateId,
      channel: "pos",
      status: "completed",
      currency: "USD",
      items: cartItems.map((item) => ({
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity,
        unitPriceCents: item.unitPriceCents,
        discountCents: 0,
      })),
      tenders: [
        {
          method: tenderMethod,
          amountCents: cartSubtotal,
        },
      ],
    });
  };

  const isLoading = catalogQuery.isLoading || associatesQuery.isLoading || registersQuery.isLoading;
  const hasError = catalogQuery.isError || associatesQuery.isError || registersQuery.isError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-emerald-500 dark:text-emerald-300" />
            {t("retail.quickActions.sale.title", { defaultValue: "Quick sale" })}
          </DialogTitle>
          <DialogDescription>
            {t("retail.quickActions.sale.description", {
              defaultValue: "Scan or select products to record a counter sale in seconds.",
            })}
          </DialogDescription>
        </DialogHeader>

        {hasError ? (
          <div className="rounded-lg border border-rose-200/70 bg-rose-50/60 p-3 text-sm text-rose-600 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200">
            {t("retail.quickActions.sale.loadError", {
              defaultValue: "Checkout data is unavailable right now.",
            })}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-4 rounded-xl border border-slate-200/70 bg-white/95 p-4 dark:border-slate-800/60 dark:bg-slate-950/55">
              <div className="grid gap-3 md:grid-cols-[minmax(0,0.5fr)_minmax(0,0.25fr)_minmax(0,0.25fr)]">
                <div className="space-y-2">
                  <Label>{t("retail.quickActions.sale.productLabel", { defaultValue: "Product" })}</Label>
                  {isLoading ? (
                    <Skeleton className="h-10 rounded-md" />
                  ) : (
                    <Select
                      value={selectedProductId ?? ""}
                      onValueChange={(value) => setSelectedProductId(value)}
                      disabled={productOptions.length === 0 || createTransactionMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("retail.quickActions.sale.productPlaceholder", {
                            defaultValue: "Select product",
                          })}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {productOptions.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name} · {product.sku}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="retail-quick-sale-quantity">
                    {t("retail.quickActions.sale.quantityLabel", { defaultValue: "Quantity" })}
                  </Label>
                  <Input
                    id="retail-quick-sale-quantity"
                    type="number"
                    min={1}
                    step={1}
                    value={quantity}
                    onChange={(event) => setQuantity(event.currentTarget.value)}
                    disabled={createTransactionMutation.isPending}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    className="w-full"
                    onClick={handleManualAdd}
                    disabled={createTransactionMutation.isPending}
                  >
                    {t("retail.quickActions.sale.addButton", { defaultValue: "Add to cart" })}
                  </Button>
                </div>
              </div>

              <form className="space-y-2" onSubmit={handleScanSubmit}>
                <Label htmlFor="retail-quick-sale-scan">
                  {t("retail.quickActions.sale.scanLabel", { defaultValue: "Barcode scan" })}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="retail-quick-sale-scan"
                    ref={barcodeInputRef}
                    value={barcode}
                    onChange={(event) => setBarcode(event.currentTarget.value)}
                    placeholder={t("retail.quickActions.sale.scanPlaceholder", {
                      defaultValue: "Scan or enter barcode",
                    })}
                    disabled={createTransactionMutation.isPending}
                    autoCapitalize="off"
                    autoComplete="off"
                  />
                  <Button type="submit" variant="outline" disabled={createTransactionMutation.isPending}>
                    <ScanBarcode className="mr-2 h-4 w-4" />
                    {t("retail.quickActions.sale.scanAction", { defaultValue: "Add" })}
                  </Button>
                </div>
              </form>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("retail.quickActions.sale.cartTitle", { defaultValue: "Items" })}
                </h3>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {t("retail.quickActions.sale.cartSubtotal", {
                    defaultValue: "Subtotal {{amount}}",
                    amount: formatCurrency(cartSubtotal, "USD", { isMinorUnit: true }),
                  })}
                </span>
              </div>
              {cartItems.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200/70 p-4 text-sm text-slate-500 dark:border-slate-800/60 dark:text-slate-400">
                  {t("retail.quickActions.sale.cartEmptyNotice", {
                    defaultValue: "No items added yet.",
                  })}
                </div>
              ) : (
                <div className="space-y-2">
                  {cartItems.map((item) => (
                    <div
                      key={`${item.productId}-${item.variantId ?? "base"}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200/70 bg-white/95 px-3 py-2 dark:border-slate-800/60 dark:bg-slate-950/55"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {item.name}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {formatCurrency(item.unitPriceCents, "USD", { isMinorUnit: true })}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="h-8 w-8"
                            onClick={() => handleQuantityChange(item.productId, item.variantId, -1)}
                            disabled={createTransactionMutation.isPending}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {item.quantity}
                          </span>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="h-8 w-8"
                            onClick={() => handleQuantityChange(item.productId, item.variantId, 1)}
                            disabled={createTransactionMutation.isPending}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-slate-500 hover:text-rose-500"
                          onClick={() => handleRemoveItem(item.productId, item.variantId)}
                          disabled={createTransactionMutation.isPending}
                          aria-label={t("retail.quickActions.sale.removeItem", { defaultValue: "Remove item" })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form className="space-y-4 rounded-xl border border-slate-200/70 bg-white/95 p-4 dark:border-slate-800/60 dark:bg-slate-950/55" onSubmit={handleSubmit}>
              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>
                    {t("retail.quickActions.sale.tenderLabel", { defaultValue: "Tender" })}
                  </Label>
                  <Select value={tenderMethod} onValueChange={(value) => setTenderMethod(value as typeof tenderMethod)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="card">
                        {t("retail.checkout.cart.tenders.card", { defaultValue: "Card" })}
                      </SelectItem>
                      <SelectItem value="cash">
                        {t("retail.checkout.cart.tenders.cash", { defaultValue: "Cash" })}
                      </SelectItem>
                      <SelectItem value="gift">
                        {t("retail.checkout.cart.tenders.gift", { defaultValue: "Gift" })}
                      </SelectItem>
                      <SelectItem value="store_credit">
                        {t("retail.checkout.cart.tenders.storeCredit", { defaultValue: "Store credit" })}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    {t("retail.quickActions.sale.registerLabel", { defaultValue: "Register" })}
                  </Label>
                  {isLoading ? (
                    <Skeleton className="h-10 rounded-md" />
                  ) : (
                    <Select
                      value={selectedRegisterId ?? ""}
                      onValueChange={(value) => setSelectedRegisterId(value)}
                      disabled={registers.length === 0 || createTransactionMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("retail.quickActions.sale.registerPlaceholder", {
                            defaultValue: "Select register",
                          })}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {registers.map((register) => (
                          <SelectItem key={register.id} value={register.id}>
                            {register.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>
                    {t("retail.quickActions.sale.associateLabel", { defaultValue: "Associate" })}
                  </Label>
                  {isLoading ? (
                    <Skeleton className="h-10 rounded-md" />
                  ) : (
                    <Select
                      value={selectedAssociateId ?? ""}
                      onValueChange={(value) => setSelectedAssociateId(value)}
                      disabled={associates.length === 0 || createTransactionMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t("retail.quickActions.sale.associatePlaceholder", {
                            defaultValue: "Assign associate",
                          })}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {associates.map((associate) => (
                          <SelectItem key={associate.id} value={associate.id}>
                            {associate.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="submit"
                  className="gap-2"
                  disabled={
                    createTransactionMutation.isPending ||
                    hasError ||
                    isLoading ||
                    cartItems.length === 0
                  }
                >
                  {createTransactionMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("retail.quickActions.sale.submitPending", { defaultValue: "Recording…" })}
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4" />
                      {t("retail.quickActions.sale.submitLabel", { defaultValue: "Complete sale" })}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RetailQuickSaleDialog;
